import { useMemo } from 'react';
import type { RejectionReasonOption } from '../../../hooks/useRejectionReasons';
import { SearchableMultiSelect } from '../../SearchableMultiSelect';
import { Textarea } from '../../ui/textarea';
import { ReviewDialogShell } from './ReviewDialogShell';

const NOTE_TEXTAREA_ID = 'reject-submission-note';

export interface RejectSubmissionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * `hard`: the moderator can reject directly — the note is publisher-facing and
   * goes into the rejection email. `soft`: the moderator can only route the
   * submission to another reviewer group — the note is saved as an internal note.
   */
  mode: 'soft' | 'hard';
  reasons: RejectionReasonOption[];
  isReasonsLoading: boolean;
  reasonsError: string | null;
  selectedCodes: string[];
  onSelectedCodesChange: (codes: string[]) => void;
  note: string;
  onNoteChange: (value: string) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  /** Whether the submit action is disabled — validation lives in the hook. */
  submitDisabled: boolean;
  /** Minimum note length, for the character counter (owned by the hook). */
  noteMinLength: number;
  error: string | null;
}

export function RejectSubmissionDialog({
  open,
  onOpenChange,
  mode,
  reasons,
  isReasonsLoading,
  reasonsError,
  selectedCodes,
  onSelectedCodesChange,
  note,
  onNoteChange,
  onSubmit,
  isSubmitting,
  submitDisabled,
  noteMinLength,
  error,
}: RejectSubmissionDialogProps) {
  const isHardReject = mode === 'hard';

  // Show active reasons, plus any already-selected reason even if it has since
  // been retired (mirrors the previous picker so historical selections persist).
  const choices = useMemo(
    () =>
      reasons
        .filter(
          (reason) => reason.isActive || selectedCodes.includes(reason.code),
        )
        .map((reason) => ({
          key: reason.code,
          label: reason.label,
          category: reason.category,
        })),
    [reasons, selectedCodes],
  );

  const noteLength = note.trim().length;

  if (!open) {
    return null;
  }

  return (
    <ReviewDialogShell
      title="Reject submission"
      onClose={() => onOpenChange(false)}
      closeDisabled={isSubmitting}
      footer={
        <>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="flex-1 rounded-lg bg-neutral-800/60 px-4 py-2.5 font-medium text-neutral-300 transition-colors hover:bg-neutral-800 disabled:opacity-50 sm:flex-none"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={submitDisabled}
            className="flex-1 rounded-lg bg-red-500 px-4 py-2.5 font-semibold text-white transition-colors hover:bg-red-400 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
          >
            {isSubmitting ? 'Submitting…' : isHardReject ? 'Reject' : 'Submit'}
          </button>
        </>
      }
    >
      <p className="text-sm text-neutral-400">
        {isHardReject
          ? 'Rejecting notifies the developer by email. Choose the reason(s) and add a note — your note is included in that email.'
          : 'This routes the submission to another reviewer group for a final decision. Choose the reason(s) and add an internal note for that reviewer.'}
      </p>

      <div className="space-y-1.5">
        <span className="block text-sm font-medium text-white">
          Rejection reasons
        </span>
        {isReasonsLoading ? (
          <div className="text-sm text-neutral-500">Loading reasons…</div>
        ) : reasonsError ? (
          <div className="text-sm text-red-400">{reasonsError}</div>
        ) : (
          <SearchableMultiSelect
            choices={choices}
            value={selectedCodes}
            onChange={onSelectedCodesChange}
            disabled={isSubmitting}
            groupBy={(choice) => choice.category}
            maxVisibleSelections={6}
            placeholder="Select rejection reason(s)"
            searchPlaceholder="Search reasons…"
          />
        )}
      </div>

      <div className="space-y-1.5">
        <label
          htmlFor={NOTE_TEXTAREA_ID}
          className="block text-sm font-medium text-white"
        >
          {isHardReject ? 'Note to the developer' : 'Internal note'}
        </label>
        <p
          className={
            isHardReject ? 'text-xs text-amber-300' : 'text-xs text-neutral-400'
          }
        >
          {isHardReject
            ? 'Publisher-facing — this note is included in the rejection email sent to the developer.'
            : 'Visible to reviewers only — saved as an internal note, not sent to the developer.'}
        </p>
        <Textarea
          id={NOTE_TEXTAREA_ID}
          value={note}
          onChange={(event) => onNoteChange(event.target.value)}
          rows={4}
          maxLength={5000}
          disabled={isSubmitting}
          className="border-neutral-700 bg-neutral-950 text-white placeholder:text-neutral-500"
          placeholder={`Explain the reason for rejection (min ${noteMinLength} characters)…`}
        />
        <div
          className={`py-1.5 text-xs ${noteLength < noteMinLength ? 'text-red-400/70' : 'text-neutral-500'}`}
        >
          {noteLength}/{noteMinLength} min characters
        </div>
      </div>

      {error && <div className="text-sm text-red-400">{error}</div>}
    </ReviewDialogShell>
  );
}
